import React from "react";
import { Link } from "react-router-dom";
import moment from "jalali-moment";
import BoltIcon from "@mui/icons-material/Bolt";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { EmptyState, Skeleton } from "../../../shares/dashboard/DashboardWidgets";

/**
 * "What should I do today?" - the one thing that matters most, up front.
 *
 * The old page showed three equally weighted cards and left the student to work
 * out the priority themselves. useStudentHome already ranks assignments and the
 * next exam against each other, so this just renders the winner as a single
 * unmissable action.
 */

const URGENCY = {
    overdue: {
        label: "مهلت گذشته",
        card: "border-danger bg-danger-soft",
        chip: "bg-danger text-white",
        button: "bg-danger hover:bg-danger",
    },
    today: {
        label: "مهلت امروز",
        card: "border-warning bg-warning-soft",
        chip: "bg-warning text-white",
        button: "bg-warning hover:bg-warning",
    },
    soon: {
        label: "به‌زودی",
        card: "border-rule bg-surface",
        chip: "bg-primary-soft text-primary",
        button: "bg-primary hover:bg-primary-hover",
    },
    later: {
        label: "پیش رو",
        card: "border-rule-subtle bg-surface",
        chip: "bg-primary-soft text-primary",
        button: "bg-primary hover:bg-primary-hover",
    },
};

const describeDays = (days) => {
    if (days === null || days === undefined) return "";
    if (days < 0) return `${Math.abs(days).toLocaleString("fa-IR")} روز گذشته`;
    if (days === 0) return "امروز";
    if (days === 1) return "فردا";
    return `${days.toLocaleString("fa-IR")} روز دیگر`;
};

export default function FocusCard({ focus, loading, index = 0 }) {
    if (loading && !focus) {
        return (
            <section
                className="dash-rise rounded-panel border border-rule-subtle bg-surface p-xl shadow-card"
                style={{ "--i": index }}
            >
                <Skeleton className="h-[1.4rem] w-[10rem]" />
                <Skeleton className="mt-md h-[2.6rem] w-[60%]" />
                <Skeleton className="mt-lg h-[4rem] w-[14rem]" />
            </section>
        );
    }

    if (!focus) {
        return (
            <section
                className="dash-rise rounded-panel border border-rule-subtle bg-surface p-xl shadow-card"
                style={{ "--i": index }}
            >
                <EmptyState
                    icon={TaskAltIcon}
                    title="فعلاً کاری در انتظارت نیست"
                    description="همه تکالیف و آزمون‌ها به‌روزند. وقت خوبیه برای مرور جلسه‌های قبلی."
                    actionLabel="مرور جلسات"
                    actionTo="/dashboard/classes"
                />
            </section>
        );
    }

    const isExam = focus.type === "exam";
    const tone = URGENCY[isExam ? "soon" : focus.urgency] ?? URGENCY.later;
    const date = isExam ? focus.startsAt : focus.endsAt;

    return (
        <section
            className={`dash-rise rounded-panel border p-xl shadow-card ${tone.card}`}
            style={{ "--i": index }}
        >
            <div className="flex items-center gap-xs">
                <BoltIcon sx={{ fontSize: "1.8rem" }} className="text-ink-muted" />
                <h2 className="text-sm font-medium text-ink-muted">
                    مهم‌ترین کار الان
                </h2>
                <span
                    className={`ms-auto rounded-pill px-md py-2xs text-xs ${tone.chip}`}
                >
                    {isExam ? "آزمون" : tone.label}
                </span>
            </div>

            <p className="mt-md text-2xl font-semibold text-ink">
                {isExam ? "آزمون پیش رو" : focus.title}
            </p>

            {!isExam && focus.subtitle && (
                <p className="mt-2xs text-base text-ink-muted">{focus.subtitle}</p>
            )}

            <div className="mt-sm flex flex-wrap items-center gap-x-md gap-y-2xs text-sm text-ink-subtle">
                <span className="font-medium text-ink-muted">
                    {describeDays(focus.daysLeft)}
                </span>
                {date && (
                    <span>
                        {moment(new Date(date)).locale("fa").format("DD MMMM YYYY")}
                    </span>
                )}
                {isExam && focus.duration && <span>{focus.duration}</span>}
            </div>

            <Link
                to={isExam ? "/dashboard/exams" : "/dashboard/homework"}
                className={`mt-lg inline-flex items-center rounded-soft px-xl py-sm text-base text-white transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none ${tone.button}`}
            >
                {isExam ? "مشاهده آزمون" : "انجام تکلیف"}
            </Link>
        </section>
    );
}
